/**
 * Create Inbound Dialog Component
 *
 * Dialog for creating new inbound warehouse movements
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../ui/select';
import { Textarea } from '../ui/textarea';
import {
  X,
  Trash2,
  Search,
  Loader2
} from 'lucide-react';
import warehouseService from '../../services/warehouseService';
import purchaseService from '../../services/purchaseService';
import productService from '../../services/productService';
import clientService from '../../services/clientService';
import EnhancedProductLookup from '../EnhancedProductLookup';

// Simple Combobox component for autocomplete
const Combobox = ({ options, value, onChange, getOptionLabel, placeholder, disabled, required }) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filteredOptions = options.filter(option =>
    getOptionLabel(option).toLowerCase().includes(search.toLowerCase())
  );

  const selectedLabel = value ? getOptionLabel(value) : '';

  return (
    <div className="relative">
      <Input
        value={isOpen ? search : selectedLabel}
        onChange={(e) => {
          setSearch(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        placeholder={placeholder}
        disabled={disabled}
        className={required && !value ? 'border-red-300' : ''}
      />
      {isOpen && filteredOptions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredOptions.map((option, index) => (
            <div
              key={index}
              className="px-3 py-2 cursor-pointer hover:bg-muted"
              onMouseDown={() => {
                onChange(null, option);
                setSearch('');
                setIsOpen(false);
              }}
            >
              {getOptionLabel(option)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

function CreateInboundDialog({ open, onClose, onSuccess, purchaseOrderId = null }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sourceType, setSourceType] = useState(purchaseOrderId ? 'purchase_order' : 'manual');
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [location, setLocation] = useState('main');
  const [notes, setNotes] = useState('');
  const [productLookupOpen, setProductLookupOpen] = useState(false);

  // Load initial data
  useEffect(() => {
    if (open) {
      loadInitialData();
      if (purchaseOrderId) {
        loadPurchaseOrder(purchaseOrderId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, purchaseOrderId]);

  const loadInitialData = async () => {
    try {
      // Load purchase orders
      const poResponse = await purchaseService.getPurchaseOrders({ page_size: 100, status: 'confirmed' });
      setPurchaseOrders(poResponse.purchase_orders || []);

      // Load suppliers - use correct function signature (page, pageSize, filters)
      // API returns array directly, not wrapped in object
      const supplierResponse = await purchaseService.getSuppliers(1, 100, {});
      setSuppliers(Array.isArray(supplierResponse) ? supplierResponse : []);

      // Load clients
      const clientResponse = await clientService.getClients({ page_size: 100 });
      setClients(clientResponse.clients || []);

      // Load products
      const productResponse = await productService.getProducts({ page_size: 100 });
      setProducts(productResponse.products || []);
    } catch (err) {
      console.error('Failed to load initial data:', err);
    }
  };

  const loadPurchaseOrder = async (poId) => {
    try {
      const order = await purchaseService.getPurchaseOrder(poId);
      setSelectedPurchaseOrder(order);
      setSourceType('purchase_order');

      // Pre-populate fields from purchase order
      if (order.supplier_id) {
        const supplier = suppliers.find(s =>
          s._id === order.supplier_id ||
          s.supplier_id === order.supplier_id
        );
        setSelectedSupplier(supplier);
      }

      // Pre-populate client from purchase order
      if (order.client_ref || order.client_id) {
        const client = clients.find(c =>
          c._id === order.client_ref ||
          c._id === order.client_id ||
          c.client_id === order.client_id
        );
        setSelectedClient(client);
      }

      // Convert PO products to inbound products
      const inboundProducts = order.products?.map(p => ({
        product_id: p.product_code || p.product_id || p._id,
        product_code: p.product_code || p.product_id,
        product_name: p.name || p.product_name,
        specification: p.specification || '',
        quantity_planned: p.quantity || 0,
        quantity_actual: p.quantity || 0,
        unit: p.unit || '',
        unit_price: p.unit_price || p.unit_cost || 0,
        location: 'main'
      })) || [];

      setSelectedProducts(inboundProducts);
    } catch (err) {
      setError(`Failed to load purchase order: ${err.message}`);
    }
  };

  const handleSourceTypeChange = (value) => {
    setSourceType(value);
    setSelectedPurchaseOrder(null);
    setSelectedProducts([]);
  };

  const handlePurchaseOrderSelect = (event, value) => {
    setSelectedPurchaseOrder(value);
    if (value) {
      loadPurchaseOrder(value._id || value.purchase_order_id);
    }
  };

  const handleSupplierSelect = (event, value) => {
    setSelectedSupplier(value);
  };

  const handleClientSelect = (event, value) => {
    setSelectedClient(value);
  };

  const handleProductChange = (index, field, value) => {
    const updated = [...selectedProducts];
    updated[index][field] = value;

    // Auto-fill product details when product is selected
    if (field === 'product_id' && value) {
      const product = products.find(p => p._id === value || p.product_id === value);
      if (product) {
        updated[index].product_code = product.product_code || product.product_id;
        updated[index].product_name = product.name || product.product_name;
        updated[index].specification = product.specification || '';
        updated[index].unit = product.unit || '';
      }
    }

    setSelectedProducts(updated);
  };

  const handleRemoveProduct = (index) => {
    setSelectedProducts(selectedProducts.filter((_, i) => i !== index));
  };

  const handleOpenProductLookup = () => {
    setProductLookupOpen(true);
  };

  const handleAddProductsFromLookup = (products) => {
    const newProducts = products.map(p => {
      const productCode = p.product_code || p.product_id || p._id;
      const productName = p.name || p.product_name || productCode;

      return {
        product_id: productCode,
        product_code: productCode,
        product_name: productName,
        specification: p.specification || '',
        // Manufacturing parameters
        material: p.material || '',
        coating: p.coating || '',
        tip_diameter: p.tip_diameter || '',
        total_length: p.total_length || '',
        degree: p.degree || '',
        shank_diameter: p.shank_diameter || '',
        tolerance: p.tolerance || '',
        blade_type: p.blade_type || '',
        guide_pillar_length: p.guide_pillar_length || '',
        guide_pillar_tolerance: p.guide_pillar_tolerance || '',
        marking: p.marking || '',
        // Quantity and pricing
        quantity_planned: 0,
        quantity_actual: 1,
        unit: p.unit || '',
        unit_price: p.price || p.unit_price || 0,
        location: location
      };
    });

    setSelectedProducts([...selectedProducts, ...newProducts]);
    setProductLookupOpen(false);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate required fields
      if (!selectedClient) {
        throw new Error('请选择客户');
      }

      if (!selectedSupplier && sourceType === 'manual') {
        throw new Error('请选择供应商');
      }

      if (selectedProducts.length === 0) {
        throw new Error('请添加至少一个产品');
      }

      // Prepare movement data
      let movementData;

      if (sourceType === 'purchase_order' && selectedPurchaseOrder) {
        // Create from purchase order
        const data = {
          purchase_order_id: selectedPurchaseOrder._id || selectedPurchaseOrder.purchase_order_id,
          location: location,
          notes: notes
        };

        const response = await warehouseService.createInboundFromPurchaseOrder(data);
        movementData = response.movement;
      } else {
        // Manual creation
        movementData = {
          movement_type: 'inbound',
          reference_type: 'manual',
          client_id: selectedClient?._id,
          client_ref: selectedClient?._id,
          client_name: selectedClient?.name || selectedClient?.client_name,
          supplier_id: selectedSupplier?._id || selectedSupplier?.supplier_id,
          supplier_name: selectedSupplier?.name || selectedSupplier?.supplier_name,
          location: location,
          products: selectedProducts.filter(p => p.product_id && p.quantity_actual > 0),
          notes: notes,
          status: 'confirmed'  // Inbound movements are typically confirmed immediately
        };

        const response = await warehouseService.createMovement(movementData);
        movementData = response.movement;
      }

      // Success
      if (onSuccess) {
        onSuccess(movementData);
      }
      handleClose();
    } catch (err) {
      setError(err.message || '创建失败');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setSourceType('manual');
      setSelectedPurchaseOrder(null);
      setSelectedSupplier(null);
      setSelectedClient(null);
      setSelectedProducts([]);
      setLocation('main');
      setNotes('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>创建入库单</DialogTitle>
            <Button variant="ghost" size="icon" onClick={handleClose} disabled={loading}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription className="flex items-center justify-between">
                {error}
                <Button variant="ghost" size="sm" onClick={() => setError(null)}>关闭</Button>
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Source Type Selection */}
            <div className="md:col-span-2">
              <Label>入库类型</Label>
              <Select
                value={sourceType}
                onValueChange={handleSourceTypeChange}
                disabled={loading || purchaseOrderId}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="purchase_order">从采购单创建</SelectItem>
                  <SelectItem value="manual">手动创建</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Purchase Order Selection */}
            {sourceType === 'purchase_order' && (
              <div className="md:col-span-2">
                <Label>选择采购单 *</Label>
                <Combobox
                  options={purchaseOrders}
                  getOptionLabel={(option) =>
                    `${option.purchase_order_id || option.po_number || option._id} - ${option.supplier_name || ''}`
                  }
                  value={selectedPurchaseOrder}
                  onChange={handlePurchaseOrderSelect}
                  disabled={loading || purchaseOrderId}
                  placeholder="搜索采购单..."
                  required
                />
              </div>
            )}

            {/* Client Selection (Required for both types) */}
            <div className="md:col-span-2">
              <Label>选择客户 *</Label>
              <Combobox
                options={clients}
                getOptionLabel={(option) =>
                  `${option.client_id || ''} - ${option.name || option.client_name}`
                }
                value={selectedClient}
                onChange={handleClientSelect}
                disabled={loading}
                placeholder="搜索客户..."
                required
              />
            </div>

            {/* Supplier Selection for Manual */}
            {sourceType === 'manual' && (
              <div className="md:col-span-2">
                <Label>选择供应商 *</Label>
                <Combobox
                  options={suppliers}
                  getOptionLabel={(option) =>
                    `${option.supplier_id || ''} - ${option.name || option.supplier_name}`
                  }
                  value={selectedSupplier}
                  onChange={handleSupplierSelect}
                  disabled={loading}
                  placeholder="搜索供应商..."
                  required
                />
              </div>
            )}

            {/* Warehouse Location */}
            <div>
              <Label>仓库位置</Label>
              <Input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          {/* Products Section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>产品明细</Label>
              {sourceType === 'manual' && (
                <Button
                  size="sm"
                  onClick={handleOpenProductLookup}
                  disabled={loading}
                >
                  <Search className="h-4 w-4 mr-2" />
                  选择产品
                </Button>
              )}
            </div>

            <Card className="border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr className="border-b">
                      <th className="text-left p-3 font-medium">产品编码</th>
                      <th className="text-left p-3 font-medium">产品名称</th>
                      <th className="text-left p-3 font-medium">规格</th>
                      <th className="text-left p-3 font-medium">计划数量</th>
                      <th className="text-left p-3 font-medium">实际数量</th>
                      <th className="text-left p-3 font-medium">单位</th>
                      <th className="text-left p-3 font-medium">单价</th>
                      <th className="text-left p-3 font-medium">库位</th>
                      {sourceType === 'manual' && <th className="p-3 w-12"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedProducts.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center p-8">
                          <span className="text-muted-foreground">
                            {sourceType === 'purchase_order'
                              ? '请选择采购单'
                              : '请点击"选择产品"按钮添加产品'}
                          </span>
                        </td>
                      </tr>
                    ) : (
                      selectedProducts.map((product, index) => (
                        <tr key={index} className="border-b">
                          <td className="p-3">{product.product_code}</td>
                          <td className="p-3">{product.product_name}</td>
                          <td className="p-3">{product.specification}</td>
                          <td className="p-3">{product.quantity_planned}</td>
                          <td className="p-3">
                            <Input
                              type="number"
                              value={product.quantity_actual}
                              onChange={(e) =>
                                handleProductChange(index, 'quantity_actual', parseFloat(e.target.value) || 0)
                              }
                              disabled={loading}
                              className="w-24"
                            />
                          </td>
                          <td className="p-3">{product.unit}</td>
                          <td className="p-3">
                            <Input
                              type="number"
                              value={product.unit_price}
                              onChange={(e) =>
                                handleProductChange(index, 'unit_price', parseFloat(e.target.value) || 0)
                              }
                              disabled={loading}
                              className="w-24"
                            />
                          </td>
                          <td className="p-3">
                            <Input
                              value={product.location || location}
                              onChange={(e) =>
                                handleProductChange(index, 'location', e.target.value)
                              }
                              disabled={loading}
                              className="w-24"
                            />
                          </td>
                          {sourceType === 'manual' && (
                            <td className="p-3">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveProduct(index)}
                                disabled={loading}
                                className="h-8 w-8 text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          {/* Notes */}
          <div>
            <Label>备注</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            取消
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || (sourceType === 'purchase_order' && !selectedPurchaseOrder) ||
                     (sourceType === 'manual' && (!selectedSupplier || selectedProducts.length === 0))}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                创建中...
              </>
            ) : (
              '创建入库单'
            )}
          </Button>
        </DialogFooter>

        {/* Enhanced Product Lookup Dialog */}
        <EnhancedProductLookup
          open={productLookupOpen}
          onClose={() => setProductLookupOpen(false)}
          onAddProducts={handleAddProductsFromLookup}
          clientName={selectedClient?.name || selectedClient?.client_name || ''}
          clientId={selectedClient?._id}
          currentQuotationItems={selectedProducts}
          mode="inbound"
          title="选择入库产品"
        />
      </DialogContent>
    </Dialog>
  );
}

export default CreateInboundDialog;
